import { useEffect } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, LogIn } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { PasswordInput } from "@/components/ui/PasswordInput";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import { useAuth } from "@/contexts/AuthContext";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { onboardingApi, ApiError } from "@/lib/api";

// Sign-in step inside the onboarding wizard. Lives at /onboarding/login.
// After email verification the user lands here; signing in promotes them to
// the auth-gated wizard. The remaining steps (domain branch → company →
// subdomain) all require the Firebase JWT, so the resume logic in
// OnboardingShell only fires once `user` is set.
const schema = z.object({
  email: z.string().email("Enter a valid email"),
  password: z.string().min(1, "Password required"),
});
type FormValues = z.infer<typeof schema>;

export function StepLogin() {
  const { signIn, user } = useAuth();
  const storedEmail = useOnboardingStore((s) => s.email);
  const navigate = useNavigate();

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { email: storedEmail ?? "", password: "" },
  });

  useEffect(() => {
    if (user) {
      navigate("/onboarding", { replace: true });
    }
  }, [user, navigate]);

  const onSubmit = async (values: FormValues) => {
    try {
      const user = await signIn(values.email, values.password);

      // /onboarding/login is ONLY for finishing an in-progress wizard. If the
      // user has already completed onboarding (subdomain_ready), bounce them
      // off this domain and tell them to sign in at their own subdomain —
      // day-2 access does not happen here.
      try {
        const idToken = await user.getIdToken();
        const me = await onboardingApi.me(idToken);
        if (me.stage === "subdomain_ready" && me.subdomain) {
          toast.success("Workspace already provisioned", {
            description: `Sign in at ${me.subdomain}.virtualdesk.ai instead.`,
          });
          window.location.href = `https://${me.subdomain}.virtualdesk.ai/login`;
          return;
        }
      } catch (err) {
        // 404 here means there's no onboarding row for this Firebase user —
        // unusual (a Firebase account exists but no onboarding row), but not
        // fatal. Drop them at /onboarding which will figure out the right
        // step from /me's failure path.
        const ae = err as ApiError;
        if (ae?.status && ae.status !== 404) {
          console.error("onboarding /me on login", ae);
        }
      }

      toast.success("Signed in");
      navigate("/onboarding", { replace: true });
    } catch (err) {
      const code = (err as { code?: string }).code ?? "auth/unknown";
      const friendly = friendlyFirebaseError(code);
      console.error("onboarding signIn", err);
      toast.error("Sign-in failed", { description: friendly });
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <LogIn className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Sign in to continue</CardTitle>
            <CardDescription>
              Use the email and password you set up during registration.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)}>
          <div className="space-y-1.5">
            <Label htmlFor="onb-login-email">
              Email
              <span aria-hidden="true" className="ml-1 text-signal-failed">*</span>
            </Label>
            <Input
              id="onb-login-email"
              type="email"
              autoComplete="email"
              {...form.register("email")}
            />
            {form.formState.errors.email?.message && (
              <p className="text-xs text-signal-failed">
                {form.formState.errors.email.message}
              </p>
            )}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="onb-login-password">
              Password
              <span aria-hidden="true" className="ml-1 text-signal-failed">*</span>
            </Label>
            <PasswordInput
              id="onb-login-password"
              autoComplete="current-password"
              {...form.register("password")}
            />
            {form.formState.errors.password?.message && (
              <p className="text-xs text-signal-failed">
                {form.formState.errors.password.message}
              </p>
            )}
          </div>
          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : null}
            Sign in
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function friendlyFirebaseError(code: string): string {
  switch (code) {
    case "auth/user-not-found":
    case "auth/wrong-password":
    case "auth/invalid-credential":
      return "Email or password is incorrect";
    case "auth/too-many-requests":
      return "Too many attempts — try again in a few minutes";
    case "auth/network-request-failed":
      return "Network error — check your connection";
    default:
      return "Please try again";
  }
}
